import * as React from "react";
import { Lightbox, LightboxType } from "../../../../../../../../react-core/Html/Components/Lightbox";
import { PartLoading } from "../../../../../../../../react-core/Html/PartLoading";
import { JsonDateParser } from "../../../../../../../../react-core/Utils/JsonDateParser";
import { CurrencyCode } from "../../../../../../../../Wrap/Constants/Enums/Domain/CurrencyCode";
import { PortfolioCashDetail } from "../../../../../../../../Wrap/Models/Models/PortfolioAnalysis/Cash/PortfolioCashDetail";
import { AriaTooltip } from "../../../../../../../Shared/Tooltip/AriaTooltip";
import { Action } from "../../../../../../Actions/Action";
import { AsAtDatePicker } from "../../../../../../Common/AsAtDatePicker";
import { Methods } from "../../../../../../Common/CommonHelper";

interface ICashPopupViewProps {
    showBox: boolean;
    accountSelectValue: string;
    productName: string;
    onChange: (val: boolean) => void;
}

interface ICashPopupViewState {
    isLoading?: boolean;
    startDate: Date;
    data: Array<PortfolioCashDetail>;
}

export class CashPopupView extends React.Component<ICashPopupViewProps, ICashPopupViewState> {
    constructor(props: ICashPopupViewProps) {
        super(props);
        this.state = {
            isLoading: true,
            startDate: new Date(),
            data: null,
        };
    }

    public componentDidMount() {
        this.getData();
    }

    public render(): JSX.Element {
        return <>{this.props.showBox ? this.renderModal() : null}</>;
    }

    private getData() {
        const { accountSelectValue } = this.props;
        const startDate = JsonDateParser.toJsonDateNoOffset(this.state.startDate);
        Action.getPortfolioCash(accountSelectValue, startDate, (cashDetails: Array<PortfolioCashDetail>) => {
            this.setState({
                data: cashDetails,
                isLoading: false,
            });
        });
    }

    private renderModal(): JSX.Element {
        return (
            <Lightbox
                show={this.props.showBox}
                title={this.props.productName}
                contentAfterTitle={null}
                onClose={() => this.props.onChange(false)}
                type={LightboxType.Modal}
                unclosable={false}
                noOverlay={false}
                noAutofocus={false}
                cssWrapper={"is-default-lightbox"}
            >
                {this.getModalBody()}
            </Lightbox>
        );
    }

    private renderLoading() {
        return <PartLoading isLoading={true}></PartLoading>;
    }

    private changeDate() {
        this.setState({
            isLoading: true,
        });

        this.getData();
    }

    private getModalBody(): JSX.Element {
        const { isLoading, startDate } = this.state;
        const { accountSelectValue } = this.props;
        let elementRender: JSX.Element = this.renderLoading();

        if (!isLoading) {
            elementRender = <div>{this.renderTable()}</div>;
        }

        return (
            <div className="content">
                <div className="row mb-34">
                    <div className="col-12 col-sm-auto col-middle">
                        <AsAtDatePicker
                            selected={startDate}
                            accountViewSelected={accountSelectValue}
                            onChange={(d) => this.setState({ startDate: d }, () => this.changeDate())}
                        />
                    </div>
                </div>
                {elementRender}
            </div>
        );
    }

    private renderTable(): JSX.Element {
        const { data } = this.state;
        let portfolioCash: PortfolioCashDetail;
        if (data.length > 0) {
            const availableGBP = data.filter((x) => x.CurrencyCode === CurrencyCode.GBP);
            if (availableGBP) {
                portfolioCash = availableGBP[0];
            }
        }
        return (
            <div>
                <table className="simple-table has-borders">
                    <tbody>
                        <tr>
                            <td>Portfolio</td>
                            <td className="cell-right text-highlight">
                                {(portfolioCash != null ? portfolioCash.PortfolioWeight * 100 : 0).toFixed(2)}%
                            </td>
                        </tr>
                        <tr>
                            <td>
                                Currently available local &nbsp;
                                <AriaTooltip cssWrapper="is-info-icon" showCloseButton={true}>
                                    {this.renderToolTip(portfolioCash)}
                                </AriaTooltip>
                            </td>
                            <td className="cell-right text-highlight">
                                {Methods.getMoneyFormat(
                                    {
                                        Amount: portfolioCash != null ? portfolioCash.AvailBalance : 0,
                                        Currency: CurrencyCode.GBP,
                                    },
                                    false
                                )}
                            </td>
                        </tr>
                        <tr>
                            <td>Balance GBP</td>
                            <td className="cell-right text-highlight">
                                {Methods.getMoneyFormat(
                                    {
                                        Amount: portfolioCash != null ? portfolioCash.BalanceGBP : 0,
                                        Currency: CurrencyCode.GBP,
                                    },
                                    false
                                )}
                            </td>
                        </tr>
                        <tr>
                            <td>Accrued interest</td>
                            <td className="cell-right text-highlight">
                                {Methods.getMoneyFormat(
                                    {
                                        Amount: Number(portfolioCash != null ? portfolioCash.AccruedInterest : 0),
                                        Currency: CurrencyCode.GBP,
                                    },
                                    false
                                )}
                            </td>
                        </tr>
                    </tbody>
                </table>
            </div>
        );
    }
    private renderToolTip(portfolioCash: PortfolioCashDetail): JSX.Element {
        return (
            <table className="simple-table no-sided-padding">
                <tbody>
                    {this.getContent(portfolioCash != null ? portfolioCash.LedgerAvailableCash : 0, "+ Ledger balance")}
                    <tr>
                        <td>{portfolioCash != null && portfolioCash.UnsettledCash > 0 ? "- " : "+ "} Unsettled</td>
                        <td className="cell-right text-highlight">
                            {Methods.getMoneyFormat(
                                {
                                    Amount: portfolioCash != null ? portfolioCash.UnsettledCash : 0,
                                    Currency: CurrencyCode.GBP,
                                },
                                false
                            )}
                        </td>
                    </tr>
                    {this.getContent(portfolioCash != null ? portfolioCash.CmtAvailableCash : 0, "+ Call balance")}
                    {this.getContent(
                        portfolioCash != null ? portfolioCash.BuyOrdersAvailableCash : 0,
                        "- Buy order commitments"
                    )}
                    {this.getContent(portfolioCash != null ? portfolioCash.FeeCommitments : 0, "- Charge commitments")}
                    {this.getContent(
                        portfolioCash != null ? portfolioCash.CashWithdrawalsAvailableCash : 0,
                        "- Withdrawal commitments"
                    )}
                    {this.getContent(
                        portfolioCash != null ? portfolioCash.UnclearedDepositsAvailableCash : 0,
                        "+ Uncleared deposits"
                    )}
                    <tr>
                        <td>Total</td>
                        <td>
                            {Methods.getMoneyFormat(
                                {
                                    Amount: portfolioCash != null ? portfolioCash.AvailBalance : 0,
                                    Currency: CurrencyCode.GBP,
                                },
                                false
                            )}
                        </td>
                    </tr>
                </tbody>
            </table>
        );
    }

    private getContent(value: number | string, title: string): JSX.Element {
        const amount = Number(value);
        return (
            <tr>
                <td>{title}</td>
                <td className="cell-right text-highlight">
                    {Methods.getMoneyFormat({ Amount: amount, Currency: CurrencyCode.GBP }, false)}
                </td>
            </tr>
        );
    }
}
